import { BlockError } from '@/blocks.04-project/block-error/block-error';
import { LazyInit } from '@/blocks.04-project/lazy-init/lazy-init';
import debounce from 'lodash-es/debounce';
import toNumber from 'lodash-es/toNumber';

/***
 * Класс для управления инпутом
 * @example
 *
 * Аттрибуты на элементах:
 * data-control {string, default = 'passToggle'} - кнопка для управления инпутом
 * 	'clear' - очистка введенного значения
 * 	'passToggle' - переключение видимости пароля
 * 	'icon' - добавление иконки
 * 	'search' - поле с иконкой поиска и крестиком для очистки
 * data-focus-scroll {boolean, default = false} - отвечает за скролл элемента при открытии клавиатуры на мобилке
 * data-offset-scroll {number, default = 0} - отвечает за отступ для скролла на мобилке
 *
 * Классы:
 * input-control_interact - позволяет открыть кнопку очистки инпута для работы с ней
 */
class InputControl {
  /***
   * Конструктор класса
   * @param elem {HTMLElement} DOM элемент формы с классом input-control
	 * @param force {Boolean} принудительно инициализирование
   */
  constructor(elem, force) {
    this.BlockError = new BlockError({ blockName: 'InputControl', destroy: this.destroy });
    if (!elem) return this.BlockError.error('Отсутствует elem');
    this.block = elem;
    this.block.InputControl = this;
		this.block.AsyncInputControl = new Promise((resolve) => {
			this.promiseResolve = resolve;
		}).then((ins) => ins);

		this.LazyInit = new LazyInit({ ins: this, callback: this.init, addClass: 'input-control_inited' });
		this.LazyInit.init();
		if (force) this.LazyInit.earlyInit();
  }

  init = () => {
		this.formControl = this.block.querySelector('.form-control');
		if (!this.formControl) return this.BlockError.error('Отсутствует form-control');

		this.form = this.block.closest('form');

		this.control = this.block.querySelector('.input-control__button');
		if (!this.control) return this.BlockError.error('Отсутствует input-control__button');

		this.controlValue = this.control.getAttribute('data-control');
		if (!this.controlValue) return this.BlockError.error('Отсутствует аттрибут data-control');


		this.isScrollOnFocus = this.block.hasAttribute('data-focus-scroll');
		if (this.isScrollOnFocus) {
			this.offsetScroll = this.block.dataset['offsetScroll'] || 0;
			this.block.style.setProperty('--offset-scroll', this.offsetScroll + 'px');
		}

		this.control.addEventListener('click', this.clickHandler);
		switch (this.controlValue) {
			case 'clear':
				this.formControl.addEventListener('input', this.debounceToggleClear);
				this.formControl.addEventListener('change', this.debounceToggleClear);
				break;
			case 'search':
				this.formControl.addEventListener('input', this.debounceToggleClear);
				this.formControl.addEventListener('change', this.debounceToggleClear);
				break;
		}
		if (this.form) {
			this.form.addEventListener('reset', this.resetHandler);
			this.form.addEventListener('submit', this.submitHandler);
		}

		if (this.isScrollOnFocus) {
			this.formControl.addEventListener('focus', this.focusHandler);
		}

		this.promiseResolve(this);
		this.block.AsyncInputControl = this;
  };

	clickHandler = () => {
		switch(this.controlValue) {
			case 'passToggle':
				this.passToggle();
				break;
			case 'clear':
				this.clear();
				break;
			case 'search':
				this.clear();
				break;
		}
	};

	resetHandler = () => {
		switch(this.controlValue) {
			case 'passToggle':
				this.resetPassToggle();
				break;
			case 'clear':
				this.resetClear();
				break;
			case 'search':
				this.resetClear();
				break;
		}
	};

	submitHandler = () => {
		switch(this.controlValue) {
			case 'passToggle':
				this.resetPassToggle();
				break;
		}
	};


	focusHandler = () => {
		const isNotMobile = window.matchMedia('(pointer: fine)').matches;
		if (isNotMobile) return;
		this.block.scrollIntoView();
	};

	passToggle = () => this.formControl.type = this.formControl.type === 'password' ? 'text' : 'password';

	clear = () => {
		const val = this.formControl.value;
		this.formControl.value = '';
		this.control.classList.remove('input-control__button_interact');
		this.formControl.focus();
		if (val) {
			this.formControl.dispatchEvent(new CustomEvent('input', { bubbles: true, detail: 'InputControl' }));
			this.formControl.dispatchEvent(new CustomEvent('change', { bubbles: true, detail: 'InputControl' }));
		}
		this.toggleClear();
	};
	debounceToggleClear = debounce((event) => {
		if (event?.detail === 'InputControl') return;

		this.toggleClear();
	}, 250);
	toggleClear = () => this.block.classList.toggle('input-control_interact', (this.formControl.value !== ''));

	resetPassToggle = () => this.formControl.type = 'password';
	resetClear = () => this.block.classList.remove('input-control_interact');

  destroy = () => {
		if (this.LazyInit) this.LazyInit.destroy();
		if (this.control) this.control.removeEventListener('click', this.clickHandler);
		if (this.formControl) {
			this.formControl.removeEventListener('input', this.debounceToggleClear);
			this.formControl.removeEventListener('change', this.debounceToggleClear);
			this.formControl.removeEventListener('focus', this.focusHandler);
		}
		if (this.form) {
			this.form.removeEventListener('reset', this.resetHandler);
			this.form.removeEventListener('submit', this.submitHandler);
		}
    if (this.block) {
      this.block.classList.remove('input-control_inited');
      delete this.block.InputControl;
      delete this.block.AsyncInputControl;
    }
  };

  static init(container = document, force = false) {
    Array.from(container.querySelectorAll('.input-control:not(.input-control_inited)')).forEach((elem) => new InputControl(elem, force));
  }
}

document.addEventListener('DOMContentLoaded', () => InputControl.init());

// Экспорт;
window.Block.InputControl = InputControl;
